import React, { useEffect, useMemo, useState } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroMagazineSwap.css";

/**
 * HeroMagazineSwap — animation-first variation on Magazine.
 *
 * Editorial composition is identical to ``HeroMagazine``: oversized
 * title (left-anchored for ``magazine_swap``, right-anchored for
 * its mirror ``magazine_swap_right``), supporting body / CTA, and a
 * supporting image column on the opposite side with the title
 * visually overlapping into it (the magazine-cover gesture). The
 * difference is the photo column cycles through *multiple* images
 * on a slow JS interval, sliding the previous photo UP and out of
 * the frame while the next photo slides UP into place from below —
 * a rhythm that evokes turning the page of a magazine.
 *
 * Both ``magazine_swap`` and ``magazine_swap_right`` share this
 * renderer; the only difference is the row direction (``flex-row``
 * vs ``flex-row-reverse``) and which side the title's overlap
 * margin pulls toward. Same approach as ``HeroMagazine`` for the
 * static pair — keeps the rotating-photo animation owned in one
 * place and lets the picker advertise the spatial mirror as a
 * separate variant.
 *
 * Why a vertical slide (not a cross-fade like SpotlightSwap):
 *   * The magazine column is portrait-oriented relative to the row.
 *     A vertical translation reads naturally inside that aspect; a
 *     horizontal slide would compete with the row's reading
 *     direction (text-to-image), and a cross-fade would feel
 *     identical to ``spotlight_swap`` and rob this mode of a
 *     distinct identity.
 *   * "Page turn" is the canonical magazine gesture. A vertical
 *     slide is the closest non-skeuomorphic equivalent — it ties
 *     the animation directly back to the mode's editorial origin.
 *
 * Animation mechanism: each photo is mounted as its own absolutely-
 * positioned ``--frame``. The active frame carries the ``--active``
 * class (transform: translateY(0)). The frame that *just* lost
 * active status carries the ``--exit`` class (transform:
 * translateY(-100%)) so it slides UP and out. All other frames sit
 * below at translateY(100%) waiting for their turn. CSS handles the
 * actual transition; JS only swaps which frame holds which class,
 * same coordination pattern as SpotlightSwap.
 *
 * Edit-mode is treated as static (the rotation is paused) so the
 * editor's selection / hover indicators don't fight with constantly
 * changing focus.
 */

const BP_CONFIG = {
  // ``mediaPct`` mirrors HeroMagazine — the editorial layout depends
  // on the title column being roughly 55-60% wide so the oversized
  // type breathes. ``titleOverlap`` is the negative right margin we
  // apply to the title column so its bottom line drifts into the
  // photo column on desktop. ``contentPx`` is the horizontal padding
  // inside each column.
  extra_large: { horizontal: true, mediaPct: "42%", contentPx: 64, titleOverlap: 32 },
  large:       { horizontal: true, mediaPct: "44%", contentPx: 56, titleOverlap: 28 },
  medium:      { horizontal: true, mediaPct: "46%", contentPx: 40, titleOverlap: 20 },
  small:       { horizontal: false, mediaMaxH: "38vh", contentPx: 24, contentPy: 28, titleOverlap: 0 },
  extra_small: { horizontal: false, mediaMaxH: "32vh", contentPx: 16, contentPy: 24, titleOverlap: 0 },
};

// Same cadence as SpotlightSwap. Long enough for each photo to be
// looked at; short enough that the page doesn't feel static after the
// first cycle. If we change one, we should change both — a different
// rhythm between the two swap modes would feel arbitrary.
const SWAP_INTERVAL_MS = 4500;

// With a single image there's nothing to rotate; with two images
// the slide reads as the same image flickering rather than a turn.
const MIN_FOR_SWAP = 2;

// Mirror of ``HeroMagazine``'s ``isTitleRightAnchored``. Used to
// pick the row direction (``flex-row`` vs ``flex-row-reverse``)
// and the side the title's overlap margin pulls toward. Living
// next to ``MIN_FOR_SWAP`` rather than imported from
// ``HeroMagazine`` keeps the two renderers independently
// modifiable — the static and animated families share a *concept*
// but not a runtime dependency, so a future change to one
// shouldn't silently re-shape the other.
//
// Both motion stories (slide-up swap and cross-fade) carry the
// same right-anchored convention via the ``_right`` suffix —
// ``magazine_swap_right`` and ``magazine_fade_right`` — so we
// match on the suffix rather than enumerating ids. Adding a
// future motion variant (``magazine_<motion>_right``) will pick
// up the right-anchoring rule automatically without touching this
// helper.
function isTitleRightAnchored(modeId) {
  return typeof modeId === "string" && modeId.endsWith("_right");
}

// Cross-fade vs. slide-up: ``magazine_fade`` and
// ``magazine_fade_right`` share the rotating-photo behaviour with
// the swap pair (same JS hook, same cadence, same active /
// previous frame coordination) but each frame's transition is
// opacity-only — no vertical translation. Detected purely from
// the id so the backend definitions don't need to encode any
// renderer hint; the prefix is the contract. Living here next to
// ``isTitleRightAnchored`` keeps both id-derived flags adjacent so
// a reader sees the full set of mode-id branches at a glance.
function isFadeVariant(modeId) {
  return typeof modeId === "string" && modeId.startsWith("magazine_fade");
}

// ---------------------------------------------------------------------------
// useMagazineSlideRotation
// ---------------------------------------------------------------------------
//
// Wrapper around ``setInterval`` that rotates ``activeIndex`` through
// ``[0, count)`` on a fixed cadence and *also* exposes the previously-
// active index so the renderer knows which frame should be sliding UP
// (translateY(-100%)) versus sitting BELOW (translateY(100%)) waiting
// for its turn. Without that distinction the animation would dissolve
// to a hard cut — every non-active frame would just be hidden and the
// "slide" would never have an exit phase.
//
// Pauses (matches SpotlightSwap):
//   * count < MIN_FOR_SWAP — no rotation possible
//   * prefers-reduced-motion — accessibility
//   * document.hidden — don't burn cycles in background tabs and
//     don't surprise the user with a different photo when they return

function useMagazineSlideRotation(count, { paused = false } = {}) {
  const [activeIndex, setActiveIndex] = useState(0);
  // ``prevIndex`` is what *was* active immediately before the most
  // recent tick. We don't seed it (set to ``null``) so the first
  // mount doesn't trigger a phantom "exit" animation on a frame
  // that never actually appeared.
  const [prevIndex, setPrevIndex] = useState(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false
    );
  }, []);

  // If the gallery shrinks past the current index, snap back to 0
  // (and clear ``prevIndex`` so we don't try to animate a frame that
  // no longer exists). Mirrors the same defensive reset that
  // SpotlightSwap's hook does.
  useEffect(() => {
    if (activeIndex >= count) {
      setActiveIndex(0);
      setPrevIndex(null);
    }
  }, [count, activeIndex]);

  useEffect(() => {
    if (paused || prefersReducedMotion || count < MIN_FOR_SWAP) return;

    let intervalId = null;
    const tick = () => {
      setActiveIndex((prev) => {
        // Capture the outgoing active index here (inside the setter)
        // rather than reading from state — avoids a dependency loop
        // and guarantees ``prevIndex`` reflects the index that just
        // lost focus, even if React batches the two setters.
        setPrevIndex(prev);
        return (prev + 1) % count;
      });
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(tick, SWAP_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    if (typeof document !== "undefined" && !document.hidden) {
      start();
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [count, paused, prefersReducedMotion]);

  return { activeIndex, prevIndex };
}

// ---------------------------------------------------------------------------
// HeroMagazineSwap main renderer
// ---------------------------------------------------------------------------

const HeroMagazineSwap = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const gallerySlotData = heroSlots?.getSlot("gallery");
  // ``max_count > 1`` slots serialize as an array; downgrade other
  // shapes defensively so the renderer only ever iterates a flat list.
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    () => galleryItems.filter((it) => it?.type === "image"),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isHorizontal = config.horizontal && hasImages;
  const titleOnRight = isTitleRightAnchored(item?.hero_mode);
  // ``isFade`` swaps the per-frame transition style from the
  // slide-up translation (active = translateY(0), exit =
  // translateY(-100%), default = translateY(100%)) to an
  // opacity-only cross-fade (active = opacity 1, everything else =
  // opacity 0, no transform). The frame coordination — which
  // index is ``--active`` and which is ``--exit`` — is identical
  // in both motion stories (the JS hook is shared); only the CSS
  // styling on the stage flips. Stamping the flag onto the stage
  // (rather than every frame individually) keeps the modifier
  // narrow + lets the cascade do the per-child override in CSS.
  const isFade = isFadeVariant(item?.hero_mode);

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  const headingAnim = useHeroAnimation(isAnimated, entrance, { delay: 0 });
  const bodyAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });
  const ctaAnim = useHeroAnimation(isAnimated, entrance, { delay: 400 });

  const { activeIndex, prevIndex } = useMagazineSlideRotation(images.length);

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Empty state mirrors HeroMagazine's "no media" fallback: stack the
  // text centred so the editor still has something readable while the
  // user is wiring up images.
  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            className="flex flex-col gap-4 w-full max-w-3xl mx-auto"
            style={{ padding: config.contentPx, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty
                gallery slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${
          isHorizontal
            ? titleOnRight ? "flex flex-row-reverse" : "flex flex-row"
            : titleOnRight ? "flex flex-col-reverse" : "flex flex-col"
        } w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Text column. Identical sizing rules to HeroMagazine: the
            negative ``titleOverlap`` margin lets the bottom of the
            heading drift into the photo column on desktop — the
            editorial "magazine cover" gesture. We pull the title
            toward whichever side the photo column sits on (left
            for ``magazine_swap``, right for
            ``magazine_swap_right``), mirroring HeroMagazine's
            handling of its own static pair. ``z-10`` ensures the
            title overlay reads above the photo column even when it
            overflows. ``min-w-0`` keeps the column honouring its
            ``flex-1`` allocation rather than ballooning to its
            widest child's intrinsic width (oversized titles love to
            do this). */}
        <div
          className="flex flex-col justify-center flex-1 min-w-0 relative z-10"
          style={{
            ...(isHorizontal
              ? {
                  paddingLeft: config.contentPx,
                  paddingRight: config.contentPx,
                  ...(titleOnRight
                    ? { marginLeft: -config.titleOverlap }
                    : { marginRight: -config.titleOverlap }),
                }
              : {
                  paddingLeft: config.contentPx,
                  paddingRight: config.contentPx,
                  paddingTop: config.contentPy,
                  paddingBottom: config.contentPy,
                }),
          }}
        >
          <div
            ref={headingAnim.ref}
            className={headingAnim.className}
            style={{ ...headingAnim.style, marginBottom: config.titleOverlap > 0 ? 8 : 16 }}
          >
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          </div>
          <div ref={bodyAnim.ref} className={bodyAnim.className} style={{ ...bodyAnim.style, marginBottom: 16 }}>
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          </div>
          <div ref={ctaAnim.ref} className={ctaAnim.className} style={ctaAnim.style}>
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>

        {/* Photo column. Same sizing model as HeroMagazine's media
            column: no explicit height on desktop (let the row's
            minHeight + flexbox stretch governs), explicit minHeight/
            maxHeight + ``order: -1`` on mobile so the column
            actually appears (in a stacked column the only child is
            ``position: absolute`` and contributes nothing to flow).

            We always render a *stage* (the
            ``.hero-magazine-swap-stage``) — even with a single
            image — because the stage owns the clipping rectangle
            that keeps the slide animation from leaking past the
            column boundary. With one image the slide never engages
            (``MIN_FOR_SWAP = 2``) so the stage just shows a static
            frame; the cost is a single extra wrapper div. */}
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={
            isHorizontal
              ? { width: config.mediaPct }
              : {
                  width: "100%",
                  minHeight: config.mediaMaxH,
                  maxHeight: config.mediaMaxH,
                  // Same mobile-stacking rule as HeroMagazine: keep
                  // photos on top of the text regardless of which
                  // side the title sits on at desktop. Canonical
                  // (``flex-col``) needs ``order: -1`` to lift the
                  // second-source-order photo column above the
                  // text; the right-anchored mirror uses
                  // ``flex-col-reverse`` which already inverts source
                  // order, so applying ``order: -1`` on top of that
                  // would double-flip and put text back on top. See
                  // the matching note in HeroMagazine.
                  ...(titleOnRight ? null : { order: -1 }),
                }
          }
        >
          <div
            className={`hero-magazine-swap-stage ${
              isFade ? "hero-magazine-swap-stage--fade" : ""
            }`.trim()}
          >
            {images.map((img, idx) => {
              const src = img.cropped_image_url || img.image_url;
              // Determine which CSS class this frame carries:
              //   * active   → translateY(0), opacity 1
              //   * exit     → translateY(-100%), opacity 0
              //                (was just active, now sliding up + out)
              //   * default  → translateY(100%), opacity 0
              //                (sitting below, waiting)
              //
              // Without the explicit ``--exit`` class, every non-active
              // frame would default to "below" — the previous photo
              // would jump straight to the bottom of the stack instead
              // of sliding up out of view, killing the page-turn feel.
              let frameClass = "hero-magazine-swap-frame";
              if (idx === activeIndex) {
                frameClass += " hero-magazine-swap-frame--active";
              } else if (idx === prevIndex) {
                frameClass += " hero-magazine-swap-frame--exit";
              }
              return (
                <div
                  key={`magazine-swap-${img.id || idx}-${idx}`}
                  className={frameClass}
                >
                  {src ? (
                    (() => {
                      const { className: filterClass, style: filterStyle } =
                        getHeroImageStyle(img);
                      return (
                        <ProgressiveHeroImage
                          src={src}
                          alt={img.alt_text || ""}
                          loading={idx === 0 ? "eager" : "lazy"}
                          draggable={false}
                          className={filterClass}
                          style={filterStyle}
                        />
                      );
                    })()
                  ) : (
                    <div className="hero-magazine-swap-frame__empty" />
                  )}
                  <EditableSlotMediaOverlay slotData={img} heroItem={item} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroMagazineSwap);
